import React, { Component } from "react";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";

class Pagination extends Component {
  render() {
    const { currentPage, totalPages, onPageChange } = this.props;
    const maxPagesToShow = 5;

    // Generate page numbers
    const pages = [];
    let startPage = 1;
    let endPage = totalPages;

    // Adjust startPage and endPage if there are too many pages
    if (totalPages > maxPagesToShow) {
      const halfMaxPages = Math.floor(maxPagesToShow / 2);
      startPage = Math.max(currentPage - halfMaxPages, 1);
      endPage = Math.min(currentPage + halfMaxPages, totalPages);

      if (endPage - startPage < maxPagesToShow - 1) {
        if (currentPage <= halfMaxPages) {
          endPage = Math.min(maxPagesToShow, totalPages);
        } else if (currentPage >= totalPages - halfMaxPages) {
          startPage = totalPages - maxPagesToShow + 1;
        }
      }
    }

    // Add first page and ellipsis if necessary
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          className={`page-number ${1 === currentPage ? "active" : ""}`}
          onClick={() => onPageChange(1)}
          disabled={1 === currentPage}
        >
          1
        </button>
      );
      
      if (startPage > 2) {
        pages.push(
          <div key="start-ellipsis" className="ellipsis">
            <MoreHorizontal size={16} />
          </div>
        );
      }
    }

    // Generate page numbers within the range
    for (let i = startPage; i <= endPage; i++) {
      if (i === 1 && startPage === 1) {
        pages.push(
          <button
            key={i}
            className={`page-number ${i === currentPage ? "active" : ""}`}
            onClick={() => onPageChange(i)}
            disabled={i === currentPage}
          >
            {i}
          </button>
        );
      } else if (i !== 1) {
        pages.push(
          <button
            key={i}
            className={`page-number ${i === currentPage ? "active" : ""}`}
            onClick={() => onPageChange(i)}
            disabled={i === currentPage}
          >
            {i}
          </button>
        );
      }
    }

    // Add ellipsis and last page if necessary
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <div key="end-ellipsis" className="ellipsis">
            <MoreHorizontal size={16} />
          </div>
        );
      }
      
      pages.push(
        <button
          key={totalPages}
          className={`page-number ${totalPages === currentPage ? "active" : ""}`}
          onClick={() => onPageChange(totalPages)}
          disabled={totalPages === currentPage}
        >
          {totalPages}
        </button>
      );
    }

    return (
      <div className="modern-pagination">
        <style jsx>{`
          .modern-pagination {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            margin: 2rem 0;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }

          .pagination-container {
            display: flex;
            align-items: center;
            gap: 0.25rem;
            background: white;
            border-radius: 12px;
            padding: 0.5rem;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            border: 1px solid #e2e8f0;
          }

          .nav-button {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            border: none;
            border-radius: 8px;
            background: transparent;
            color: #64748b;
            cursor: pointer;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            font-weight: 500;
          }

          .nav-button:hover:not(:disabled) {
            background: linear-gradient(135deg, #189397 0%, #4fc1c0 100%);
            color: white;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
          }

          .nav-button:disabled {
            color: #cbd5e1;
            cursor: not-allowed;
            opacity: 0.5;
          }

          .nav-button:active:not(:disabled) {
            transform: translateY(0);
          }

          .page-number {
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 40px;
            height: 40px;
            padding: 0 0.5rem;
            border: none;
            border-radius: 8px;
            background: transparent;
            color: #475569;
            cursor: pointer;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            font-weight: 500;
            font-size: 0.875rem;
            position: relative;
            overflow: hidden;
          }

          .page-number::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
            transition: left 0.5s;
          }

          .page-number:hover:not(:disabled):not(.active)::before {
            left: 100%;
          }

          .page-number:hover:not(:disabled):not(.active) {
            background: #f8fafc;
            color: #334155;
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }

          .page-number.active {
            background: linear-gradient(135deg, #189397 0%, #4fc1c0 100%);
            color: white;
            font-weight: 600;
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
            transform: translateY(-1px);
          }

          .page-number:disabled {
            cursor: default;
          }

          .ellipsis {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            color: #94a3b8;
          }

          .info-text {
            color: #64748b;
            font-size: 0.875rem;
            margin-left: 1rem;
            font-weight: 500;
          }

          @media (max-width: 640px) {
            .modern-pagination {
              flex-wrap: wrap;
              gap: 0.25rem;
            }
            
            .pagination-container {
              padding: 0.375rem;
              gap: 0.125rem;
            }
            
            .nav-button,
            .page-number {
              width: 36px;
              height: 36px;
              min-width: 36px;
              font-size: 0.8rem;
            }
            
            .info-text {
              margin-left: 0;
              margin-top: 0.5rem;
              width: 100%;
              text-align: center;
            }
          }

          /* Smooth page transitions */
          .pagination-container {
            animation: fadeIn 0.3s ease-out;
          }

          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* Ripple effect for buttons */
          .page-number:active:not(:disabled),
          .nav-button:active:not(:disabled) {
            position: relative;
          }

          .page-number:active:not(:disabled)::after,
          .nav-button:active:not(:disabled)::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0;
            height: 0;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.6);
            transform: translate(-50%, -50%);
            animation: ripple 0.6s ease-out;
          }

          @keyframes ripple {
            to {
              width: 100px;
              height: 100px;
              opacity: 0;
            }
          }
        `}</style>

        <div className="pagination-container">
          <button
            className="nav-button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <ChevronLeft size={18} />
          </button>

          {pages}

          <button
            className="nav-button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        <div className="info-text">
          Page {currentPage} of {totalPages}
        </div>
      </div>
    );
  }
}

export default Pagination;